import { Controller } from '@hotwired/stimulus'
import { InputBox, CustomButton, Component, UserData, UserDataValue } from '../../api/models/types'
import computedValues from '../../helpers/computedValues'
import updateValidationUI, {
  ValidationParent,
  reportValidation,
} from '../../helpers/controllerHelpers/customValidationUI'
import {
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { validateInputBox } from '../../helpers/validation'
import jitsu from '../../helpers/jitsu'
import { debounce } from '../../helpers/debounce'
import custom_button_controller from './custom_button_controller'

const debouncedTrack = debounce(function (
  flowId: string,
  componentId: string,
  componentKey: string,
  value: string
) {
  jitsu.track('input:typed', {
    flow_id: flowId,
    input_id: componentId,
    input_key: componentKey,
    input_value: value,
  })
},
500) as (flowId: string, componentId: string, componentKey: string, value: string) => void

export default class extends Controller {
  static targets = ['label', 'input']
  static values = {
    inputType: String,
    validationFormula: String,
    // userData: Object,
    required: Boolean,
  }

  component: InputBox
  disconnectUserData?: () => void
  declare hasInputTarget: boolean
  declare userDataValue: UserDataValue
  declare requiredValue: boolean
  declare inputTypeValue: string
  declare inputTargets: HTMLFormElement[]
  declare validationFormulaValue: string
  declare inputTarget: HTMLFormElement
  declare dispatchUpdateDebouncer: (key, newValue) => void

  isValid: boolean
  flowEl: Element | null
  pageId: string

  declare autoAdvanceOnInputEnter: boolean
  autoAdvanceOnKeyUpHandler: (event: Event) => void

  connect() {
    const { component, flowEl, pageId } = getComponentContext(this)
    const flow = getFlow(this, true)
    this.autoAdvanceOnInputEnter = !!flow?.auto_advance_on_input_enter
    this.flowEl = flowEl
    this.pageId = pageId
    this.component = component as InputBox
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    if (this.hasInputTarget) {
      const key = this.element.getAttribute('data-component-key-value')
      const message = key && this.getValidityMessage(this.userDataValue[key])
      this.element.setAttribute('customValidityMessage', `${message}`)
      if (this.requiredValue && message)
        updateValidationUI(
          this,
          message,
          this.element as ValidationParent,
          this.inputTarget as unknown as HTMLObjectElement
        )
      if (key && this.userDataValue[key]) {
        if (this.inputTypeValue === 'checkbox') {
          this.inputTarget.checked = Boolean(this.userDataValue[key])
        } else if (this.component.phone_display_format) {
          let value = this.formatValue(String(this.userDataValue[key]), 'display')
          this.inputTarget.value = value
        } else {
          this.inputTargets.forEach((el) => (el.value = this.userDataValue[key]))
          // this.inputTarget.value = this.userDataValue[key]
        }
      }
      this.updateRanges()
    }
    lifecycleTrigger(this, 'mounted')

    if (this.autoAdvanceOnInputEnter) {
      const self = this

      this.autoAdvanceOnKeyUpHandler = function (event) {
        if ((event as KeyboardEvent).key === 'Enter') {
          if (self.component.disable_auto_next === true) return

          const pageComponents = flow.pages.find((page) => page.id === pageId)?.components
          const globalComponents = flow.components
          const pageAndGlobalComponents = [...(pageComponents || []), ...(globalComponents || [])]
          const allInputAndButtonComponents = pageAndGlobalComponents?.filter(
            (component: any) =>
              (component.type === 'InputBox' && component.disable_auto_next !== true) ||
              (component.type === 'CustomButton' &&
                component.hide !== true &&
                component.action !== 'prev-page')
          )

          let relevantComponents = allInputAndButtonComponents.filter((component) => {
            // we need to check the styles of each component to see if it's display is none
            const element = document.querySelector(`[data-component-id-value="${component.id}"]`)
            if (!element) return null
            const elementStyles = window.getComputedStyle(element).display
            if (elementStyles === 'none') {
              return null
            } else {
              return component
            }
          })

          const primaryButton = relevantComponents.find(
            (component: Component) =>
              component.type === 'CustomButton' &&
              (component as CustomButton).primary_button === true
          )

          if (primaryButton) {
            // remove all other buttons from the relevantComponents array that are not the primary button but leave components that are not buttons
            relevantComponents = relevantComponents.filter((component) => {
              if (component.type === 'CustomButton') {
                return component.id === primaryButton.id
              } else {
                return component
              }
            })
          }

          const currentComponentIndex = relevantComponents.findIndex(
            (component) => component.id === self.component.id
          )

          const nextComponent = relevantComponents[currentComponentIndex + 1]

          if (nextComponent.type === 'CustomButton') {
            // we need to trigger the button action
            const button = document.querySelector(`[data-component-id-value="${nextComponent.id}"]`)
            const buttonController = self.application.getControllerForElementAndIdentifier(
              button as Element,
              'components--custom-button'
            ) as custom_button_controller
            buttonController?.performButtonAction(event)
          } else if (nextComponent.type === 'InputBox') {
            // we need to focus the input
            const input = document.querySelector(
              `[data-component-id-value="${nextComponent.id}"] input`
            ) as HTMLInputElement
            input.focus()
          }
        }
      }

      this.element.addEventListener('keyup', this.autoAdvanceOnKeyUpHandler)
    }

    this.dispatchUpdateDebouncer = debounce(function (key, newValue) {
      this.dispatch('update', {
        detail: { key, value: newValue, callback: () => this.triggerOnChangeActions(newValue) },
      })
    }, this.component?.debounce_time) as (key, newValue) => void
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (this.autoAdvanceOnInputEnter)
      this.element.removeEventListener('keyup', this.autoAdvanceOnKeyUpHandler)
  }

  userDataValueChanged(newData: { [x: string]: string }, oldData: { [x: string]: string }) {
    const { flowId } = getComponentContext(this)
    /* Will run on every user data update */
    this.updateRanges()
    const key = String(this.element.getAttribute('data-component-key-value'))
    const old_value = oldData[key]
    const value = newData?.[key]
    if (value !== undefined) this.updateInputs(value)

    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.pageId)
    const componentIndex = flow?.pages[pageIndex]?.components.findIndex(
      (component) => component.id === this.component?.id
    )
    // only log if the value has changed
    if (value !== old_value && !doNotStoreComponentDataInCloud(this.component)) {
      window.Savvy.addEventLogs?.([
        {
          flow_id: flowId,
          event_type: 'input:typed',
          timestamp: Date.now(),
          user_data_snapshot: this.userDataValue as unknown as UserData,
          data: {
            new_input_value: value,
            old_input_value: old_value,
            component_id: this.component?.id,
            component_key: this.component?.key,
            component_path: [
              ...(this.pageId ? ['pages', `${pageIndex}`] : []),
              'components',
              `${componentIndex}`,
            ],
          },
        },
      ])
      debouncedTrack(flowId, this.component.id, this.component.key, value)
    }
  }

  updateValidStatus(event: {
    preventDefault: () => void
    params: { key: string }
    target: { type: string; checked: boolean; value: string }
  }) {
    event.preventDefault()
    const key = event.params.key
    const isCheckbox = event.target.type === 'checkbox'
    const isNumber = event.target.type === 'number'
    const inputValue = isCheckbox ? event.target.checked : event.target.value

    //* I format the value for display if the input type is not password
    const valueForDisplay = this.formatValue(String(inputValue), 'display')
    if (valueForDisplay !== inputValue && this.inputTypeValue !== 'password') {
      event.target.value = valueForDisplay
    }

    //* I dispatch the update to userDataValue with formatValue for store
    let newValue = this.formatValue(String(inputValue), 'store')

    newValue = isNumber ? Number(newValue) : newValue

    if (!isCheckbox) {
      const message = this.getValidityMessage(newValue.toString())
      if (this.hasInputTarget)
        updateValidationUI(
          this,
          message,
          this.element as ValidationParent,
          this.inputTarget as unknown as HTMLObjectElement
        )
    }
    this.dispatchUpdate(key, newValue)
  }

  triggerFlowValidation() {
    if (!this.hasInputTarget || !this.component) return
    const key = this.component.key
    let inputValue = this.inputTarget.value

    if (this.component.input_type === 'range' && this.inputTargets[1]?.value) {
      inputValue = this.inputTargets[1].value
    }

    let value = this.formatValue(inputValue, 'store')
    if (this.component.trim_whitespace_on_blur) {
      value = value.trim()
      this.dispatchUpdate(key, value)
    }

    if (this.component.format_email_on_blur) {
      value = value.split(' ').join('').toLowerCase()
      this.dispatchUpdate(key, value)
    }

    if (this.component.validate_on_blur) {
      //* using displayed value here so on blur the user gets the error message if the phone number is not valid and displayed value is not the same as the user data value
      if (this.inputTypeValue === 'tel') value = this.inputTarget.value
      const message = this.getValidityMessage(value)
      this.element.setAttribute('show-validity-message', message ? 'true' : 'false')
      if (getFlow(this).disable_form_validation) {
        if (message) {
          updateValidationUI(
            this,
            message,
            this.element as ValidationParent,
            this.inputTarget as unknown as HTMLObjectElement
          )
        }
      } else {
        reportValidation(this.inputTarget as unknown as HTMLObjectElement)
      }
    }
  }

  dispatchUpdate(key, newValue) {
    if (this.component.debounce_time) {
      this.dispatchUpdateDebouncer(key, newValue)
    } else {
      this.dispatch('update', {
        detail: { key, value: newValue, callback: () => this.triggerOnChangeActions(newValue) },
      })
    }
  }

  getValidityMessage(text: string, forceValidState?: string | undefined) {
    const component = this.component
    const defaultValidValue = text
      ? validateInputBox(component, text, this.isValid, this.userDataValue)
      : !this.requiredValue

    const isValid = forceValidState === undefined ? defaultValidValue : forceValidState
    if (isValid) return ''
    if (!text && component.empty_invalid_message) {
      return component.empty_invalid_message
    }
    if (text && component.invalid_message) {
      return component.invalid_message
    }

    switch (this.validationFormulaValue) {
      case 'email_address':
      case 'business_email':
        return 'Please enter a valid email address.'

      case 'full_name':
        return 'Please enter your full name.'

      case 'phone_number_us':
        return 'Please enter a valid US phone number.'

      case 'number_range':
        if (
          (component.integer_only ||
            (component.range_step && Number.isInteger(parseFloat(`${component.range_step}`)))) &&
          !Number.isInteger(this.userDataValue[this.component.key])
        ) {
          return `Please enter a valid integer number.`
        }
        if (component.range_min !== undefined && component.range_max === undefined)
          return `Please enter a valid number that is ${component.range_min} or above.`
        if (component.range_min === undefined && component.range_max !== undefined)
          return `Please enter a valid number that is ${component.range_max} or below.`
        if (component.range_min !== undefined && component.range_max !== undefined)
          return `Please enter a valid number between ${component.range_min} and ${component.range_max}.`
        else return 'Please enter a valid number.'

      case 'date_range':
        if (component.range_min !== undefined && component.range_max === undefined)
          return `Please enter a valid date that is after ${this.formatDate(component.range_min)}.`
        if (component.range_min === undefined && component.range_max !== undefined)
          return `Please enter a valid date that is before ${this.formatDate(component.range_max)}.`
        if (component.range_min !== undefined && component.range_max !== undefined)
          return `Please enter a valid date between ${this.formatDate(
            component.range_min
          )} and ${this.formatDate(component.range_max)}.`
        else return 'Please enter a valid date.'

      default:
        return 'Please fill out this field.'
    }
  }

  formatDate(date) {
    const dateArray = date.split('-')
    const months = {
      '01': 'January',
      '02': 'February',
      '03': 'March',
      '04': 'April',
      '05': 'May',
      '06': 'June',
      '07': 'July',
      '08': 'August',
      '09': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    }
    return this.component.input_type === 'month'
      ? `${months[dateArray[1]]} ${dateArray[0]}`
      : `${dateArray[2]}/${dateArray[1]}/${dateArray[0]}`
  }

  updateInputs(value: string) {
    if (!this.hasInputTarget) return
    if (this.inputTypeValue === 'password' || this.inputTypeValue === 'tel') return
    const isCheckbox = this.inputTypeValue === 'checkbox'

    this.inputTargets.forEach((el) => {
      isCheckbox ? (el.checked = value) : (el.value = value)
    })
  }

  formatValue(value: string, formatFor: 'store' | 'display') {
    switch (this.inputTypeValue) {
      case 'password': {
        return '********'
      }
      case 'tel': {
        return this.advancedPhoneNumberFormatting(value, formatFor)
      }
      default:
        return value
    }
  }

  updateRanges() {
    if (!this.hasInputTarget) return
    if (document.activeElement === this.inputTarget) return // Input is focused

    const component = this.component
    if (['date', 'month'].includes(this.inputTypeValue)) {
      const rangeMin = typeof component.range_min === 'string' ? component.range_min : ''
      const rangeMax = typeof component.range_max === 'string' ? component.range_max : ''
      let min = rangeMin === 'future' ? undefined : computedValues(rangeMin, this.userDataValue)
      let max = rangeMax === 'past' ? undefined : computedValues(rangeMax, this.userDataValue)
      if (component.range_min === 'future' || component.range_max === 'past') {
        const isMonth = component.input_type === 'month'
        const now = new Date()
        const year = now.getFullYear()
        const monthNum = now.getMonth() + 1
        const dayNum = now.getDate()
        const month = monthNum < 10 ? `0${monthNum}` : monthNum
        const day = dayNum < 10 ? `0${dayNum}` : dayNum
        const range = isMonth ? `${year}-${month}` : `${year}-${month}-${day}`

        if (component.range_min === 'future') min = range
        if (component.range_max === 'past') max = range
      }

      this.inputTarget.setAttribute('min', min || '')
      this.inputTarget.setAttribute('max', max || '')
    }
  }

  triggerOnChangeActions(newValue: any) {
    if (!newValue) newValue = undefined
    //* I trigger onchange actions after waiting for the userData update to complete
    if (this.component.outputs_onchange) {
      const actions = this.component.outputs_onchange
      this.dispatch('actions', {
        detail: {
          actions: actions,
          component: this.component,
        },
      })
    }
  }

  getRawNumber(phoneNumber) {
    let formattedNumber = phoneNumber

    //* Remove country code if present
    if (formattedNumber.startsWith('+')) {
      const spaceIndex = formattedNumber.indexOf(' ')
      formattedNumber =
        spaceIndex !== -1 ? formattedNumber.substring(spaceIndex + 1) : formattedNumber
    }
    if (formattedNumber.startsWith('1')) formattedNumber = formattedNumber.substring(1)
    //* Remove any formatting from the phone number
    formattedNumber = formattedNumber.replace(/\D/g, '')

    return formattedNumber
  }

  advancedPhoneNumberFormatting(phoneNumber, formatFor: 'store' | 'display') {
    //* If neither phone_display_format nor phone_store_format exist (legacy), return previous behavior
    if (!this.component.phone_display_format && !this.component.phone_store_format) {
      var cleaned = ('' + phoneNumber).replace(/\D/g, '')
      var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
      if (match) {
        var intlCode = match[1] ? '+1 ' : ''
        return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
      }
      return phoneNumber
    }

    const ruleToApply =
      formatFor === 'display'
        ? this.component.phone_display_format
        : this.component.phone_store_format

    let formatNumber: Boolean, includeCountryCode: Boolean
    switch (ruleToApply) {
      case 'no_formatting_no_country_code': {
        formatNumber = false
        includeCountryCode = false
        break
      }
      case 'no_formatting_with_country_code': {
        formatNumber = false
        includeCountryCode = true
        break
      }
      case 'formatting_no_country_code': {
        formatNumber = true
        includeCountryCode = false
        break
      }
      case 'formatting_with_country_code': {
        formatNumber = true
        includeCountryCode = true
        break
      }
      default: {
        formatNumber = false
        includeCountryCode = false
        break
      }
    }

    let formattedNumber = this.getRawNumber(phoneNumber)
    if (formattedNumber.length === 0) return ''

    //* Format the number if needed
    if (formatNumber) {
      let rawNumber = formattedNumber
      formattedNumber = '('

      const positions = [3, 6]

      for (let i = 0; i < Math.min(rawNumber.length, 10); i++) {
        formattedNumber += rawNumber[i]
        if (positions.includes(i + 1) && i + 1 < Math.min(rawNumber.length, 10)) {
          formattedNumber += i === 2 ? ') ' : '-'
        }
      }

      //* Add the rest of the number if it's longer than 10 digits
      if (rawNumber.length > 10) {
        formattedNumber += rawNumber.substring(10)
      }
    }

    //* Add country code back based on conditions
    if (includeCountryCode) {
      formattedNumber = formatNumber ? `+1 ${formattedNumber}` : `1${formattedNumber}`
    }

    return formattedNumber
  }
}
